// replaces [[featured-products]] or [[featured-products:Heading text]] placeholders
// in the article content with the featured product cards
// v2: hoists widget out of Word-paste wrappers

const FEATURED_PRODUCTS_PLACEHOLDER = /\[\[featured-products(?::([^\]]*))?\]\]/;

// defer scripts run after DOM is parsed — DOMContentLoaded may already have fired,
// so call directly rather than listening for the event
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFeaturedProductsReplacement);
} else {
  initializeFeaturedProductsReplacement();
}

function initializeFeaturedProductsReplacement() {
  const products = loadProductData();
  if (!products || products.length === 0) return;

  const contentSection = document.querySelector('.article-template__content');
  if (!contentSection) return;

  replacePlaceholder(contentSection, products);
}

function loadProductData() {
  const dataScript = document.getElementById('article-featured-products-data');
  if (!dataScript) return null;

  try {
    return JSON.parse(dataScript.textContent);
  } catch (e) {
    return null;
  }
}

function replacePlaceholder(contentSection, products) {
  const walker = document.createTreeWalker(contentSection, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const match = node.nodeValue.match(FEATURED_PRODUCTS_PLACEHOLDER);
    if (!match) continue;

    const heading = match[1] ? match[1].trim() : null;

    // split the text node around the placeholder
    const beforeText = node.nodeValue.substring(0, match.index);
    const afterText = node.nodeValue.substring(match.index + match[0].length);

    const parent = node.parentNode;

    // replace placeholder node with before-text + widget + after-text
    const beforeNode = document.createTextNode(beforeText);
    const afterNode = document.createTextNode(afterText);
    const widget = buildWidget(products, heading);

    parent.replaceChild(afterNode, node);
    parent.insertBefore(widget, afterNode);
    parent.insertBefore(beforeNode, widget);

    // hoist the widget out of any inline/block wrapper (e.g. Word-paste <span>, <p>)
    // until it is a direct child of .article-template__content
    const contentEl = document.querySelector('.article-template__content');
    while (widget.parentElement && widget.parentElement !== contentEl) {
      const wrappingEl = widget.parentElement;
      const grandParent = wrappingEl.parentNode;
      if (!grandParent) break;

      const afterSibling = wrappingEl.nextSibling;
      const remainder = [];
      let sib = widget.nextSibling;
      while (sib) { remainder.push(sib); sib = sib.nextSibling; }

      grandParent.insertBefore(widget, afterSibling);

      if (remainder.length > 0) {
        const clone = wrappingEl.cloneNode(false);
        remainder.forEach(n => clone.appendChild(n));
        grandParent.insertBefore(clone, widget.nextSibling);
      }

      if (!wrappingEl.textContent.trim() && !wrappingEl.children.length) {
        wrappingEl.remove();
      }
    }

    break; // only one replacement per article
  }
}

function buildWidget(products, heading) {
  const wrapper = document.createElement('div');
  // use the same classes as the FPL section so CSS/behaviour is identical
  wrapper.className = 'article-featured-products featured-product-list';

  if (heading) {
    const h2 = document.createElement('h2');
    h2.className = 'article-featured-products__heading';
    h2.textContent = heading;
    wrapper.appendChild(h2);
  }

  // ul + slider classes mirror featured-product-list.liquid exactly
  const grid = document.createElement('ul');
  grid.className = 'featured-product-list__grid';

  products.forEach(product => {
    grid.appendChild(buildCard(product));
  });

  wrapper.appendChild(grid);

  // dots — same classes as FPL section
  const dots = document.createElement('div');
  dots.className = 'featured-product-list__dots';
  products.forEach((_, i) => {
    const dot = document.createElement('button');
    dot.className = 'featured-product-list__dot' + (i === 0 ? ' featured-product-list__dot--active' : '');
    dot.setAttribute('aria-label', 'Product ' + (i + 1));
    dots.appendChild(dot);
  });
  wrapper.appendChild(dots);

  // give the widget a unique ID so we can find it reliably after hoisting
  const widgetId = 'article-fp-' + Date.now();
  wrapper.dataset.fpId = widgetId;

  // attach slider behaviour after the DOM settles (rAF + setTimeout 0)
  // query by data attribute so we always get the live DOM element, not a stale closure ref
  requestAnimationFrame(() => setTimeout(() => {
    const liveWrapper = document.querySelector('[data-fp-id="' + widgetId + '"]');
    if (!liveWrapper) return;
    const g = liveWrapper.querySelector('.featured-product-list__grid');
    const dotContainer = liveWrapper.querySelector('.featured-product-list__dots');
    if (!g || !dotContainer) return;

    // scroll → update active dot (same formula as FPL inline script)
    g.addEventListener('scroll', () => {
      const slideWidth = g.firstElementChild ? g.firstElementChild.offsetWidth : 0;
      if (slideWidth === 0) return;
      const gap = parseFloat(window.getComputedStyle(g).gap) || 0;
      const index = Math.round(g.scrollLeft / (slideWidth + gap));
      dotContainer.querySelectorAll('.featured-product-list__dot').forEach((d, i) =>
        d.classList.toggle('featured-product-list__dot--active', i === index)
      );
    }, { passive: true });

    // dot click → scroll slider (same formula as FPL inline script)
    dotContainer.querySelectorAll('.featured-product-list__dot').forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const slide = g.children[i];
        if (slide) {
          g.scrollTo({ left: slide.offsetLeft - g.offsetLeft, behavior: 'smooth' });
        }
      });
    });
  }, 0));

  return wrapper;
}

function buildCard(product) {
  const item = document.createElement('li');
  item.className = 'featured-product-list__item';

  const tag = product.url ? 'a' : 'div';
  const linkAttr = product.url ? `href="${escapeHtml(product.url)}"` : '';

  // USPs — same markup as card-product.liquid .card__usp
  const uspsHTML = product.usps && product.usps.length
    ? `<ul class="card__usp">
        ${product.usps.map(usp =>
          `<li class="card__usp-item"><span class="card__usp-text">${escapeHtml(usp)}</span></li>`
        ).join('')}
      </ul>`
    : '';

  // Image — same markup as card-product.liquid .card__inner
  const imageHTML = product.image
    ? `<div class="card__inner">
        <div class="card__media">
          <div class="media media--transparent">
            <img src="${escapeHtml(product.image)}"
                 alt="${escapeHtml(product.image_alt || product.title || '')}"
                 width="200" height="200" loading="lazy">
          </div>
        </div>
      </div>`
    : '';

  // Title block — primary (label/vendor) + secondary (product name)
  const titleHTML = `
    <div class="card__heading">
      <${tag} ${linkAttr} class="full-unstyled-link">
        ${product.label ? `<span class="card__heading-primary">${escapeHtml(product.label)}</span>` : ''}
        <span class="card__heading-secondary card__heading-secondary">${escapeHtml(product.title)}</span>
      </${tag}>
    </div>`;

  item.innerHTML = `
    <div class="card-wrapper">
      <div class="card card--standard card--media">
        ${imageHTML}
        <div class="card__content">
          <div class="card__information">
            <div class="card-information">
              ${titleHTML}
              ${uspsHTML}
            </div>
          </div>
        </div>
      </div>
    </div>`;

  return item;
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
